import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

interface ProgressRingProps {
  value: number;
  size?: number;
  thickness?: number;
  color?: string;
  showLogo?: boolean;
}

const ProgressRing: React.FC<ProgressRingProps> = ({ 
  value, 
  size = 80, 
  thickness = 4,
  color,
  showLogo = true
}) => {
  const theme = useTheme();
  const normalizedValue = Math.min(100, Math.max(0, value));
  const radius = (size - thickness) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (normalizedValue / 100) * circumference;
  
  const finalColor = color || theme.palette.primary.main;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
      }}
    >
      {/* Background circle */}
      <svg
        width={size}
        height={size}
        style={{ position: 'absolute', transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={theme.palette.action.disabledBackground}
          strokeWidth={thickness}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={finalColor}
          strokeWidth={thickness}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.5s ease-in-out',
          }}
        />
      </svg>
      
      {/* Center content */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {showLogo ? (
          <Box
            component="img"
            src="/logo192.png"
            alt=""
            sx={{
              width: size * 0.35,
              height: size * 0.35,
              opacity: 0.8,
              filter: 'grayscale(0.3)',
            }}
          />
        ) : (
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: size * 0.2,
              fontWeight: 700,
              color: finalColor
            }}
          >
            {normalizedValue}%
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ProgressRing;